import { useLayoutEffect, useRef, useState } from 'react';
import PromoStickyBar from '@/components/layout/PromoStickyBar';
import { ProDaysStrip } from '@/components/promo/ProDaysCounter';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useProCta } from '@/hooks/useDoubleBenefit';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Menu, X, ChevronDown, User, LogIn, LogOut, Zap, Crown, Gift, Home, Sparkles, Heart, Wallet, CalendarDays } from 'lucide-react';
import { SITE_NAME_PARTS } from '@/lib/siteConfig';
import { PROGRAM_GROUPS } from '@/constants/programs';
import { withOwnerPreview } from '@/constants/ownerPreviewPrograms';

// Шапка рисует это фото 32x32 (24x24 в мобильном меню) и обрезает его
// object-cover до квадрата. Отдельная копия — тот же центральный квадрат,
// уже обрезанный, 96x96: хватает на экраны с тройной плотностью.
// Полноразмерный оригинал остаётся у /about, /horar и старой главной,
// где он рисуется крупно.
const AUTHOR_PHOTO = "/img/bm7z03dq_Unknown-96.jpeg";

// Программы выпадающего меню. Список ОБЩИЙ с помощником — см.
// constants/programs.js: раньше у каждого был свой, и одна и та же
// программа называлась в меню и в помощнике по-разному.
//
// Меню «Программы» — 4 группы по смыслу (PROGRAM_GROUPS, 30.09.2026):
// Анализ себя · Любовь · Деньги и дело · Прогнозы. Меток «Бесплатно / ЛАЙТ /
// ПРО» нет: вход в каждую программу открыт всем, что откроется в результате —
// решает сама программа (лесенка, окно регистрации на «Рассчитать», ПРО).

// Цвет и значок каждой группы меню — как у плиток главной (NewLandingPage):
// АстроЧакры — фиолетовая, Любовь — розовая, Деньги — изумрудная; Прогнозы —
// индиго. Группа — бледная подложка своего цвета, чтобы четыре блока не
// сливались в одну простыню. Классы — целиком строками: Tailwind их ищет так.
const GROUP_STYLE = {
  self: { Icon: Sparkles, title: 'text-violet-700', icon: 'text-violet-500', box: 'bg-violet-50' },
  love: { Icon: Heart, title: 'text-rose-700', icon: 'text-rose-500', box: 'bg-rose-50' },
  money: { Icon: Wallet, title: 'text-emerald-700', icon: 'text-emerald-500', box: 'bg-emerald-50' },
  forecast: { Icon: CalendarDays, title: 'text-indigo-700', icon: 'text-indigo-500', box: 'bg-indigo-50' },
};

const MEGA_MENU_WIDTH = 960;

function GroupTitle({ group, className = '' }) {
  const st = GROUP_STYLE[group.key] || GROUP_STYLE.self;
  return (
    <span className={`flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide ${st.title} ${className}`}>
      <st.Icon className={`w-4 h-4 shrink-0 ${st.icon}`} aria-hidden="true" />
      {group.title}
    </span>
  );
}

const accountLabel = (user) => user?.name?.trim() || 'Личный кабинет';

const getStatusBadge = (user) => {
  // Use effective_tier as primary, fallback to access_level
  const tier = user?.effective_tier || user?.access_level || 1;
  const tierName = user?.effective_tier_name;
  
  if (tier === 3 || tierName === 'PRO') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
        <Crown className="w-3 h-3" />
        ПРО
      </span>
    );
  }
  if (tier === 2 || tierName === 'ASTROFEEL' || tierName === 'LITE') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-700">
        <Zap className="w-3 h-3" />
        Пробный доступ
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
      <Gift className="w-3 h-3" />
      Бесплатно
    </span>
  );
};

const MainNavbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // «Программы» в мобильном меню — раскрывающийся пункт, как выпадающий
  // список в десктопной шапке. Каждое открытие гамбургера начинается со
  // свёрнутого списка, чтобы блок входа в кабинет был виден без прокрутки.
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  // Группы внутри мобильных «Программ» — аккордеон: группа раскрывается по
  // тапу на заголовок, несколько групп можно держать открытыми сразу.
  const [mobileGroupsOpen, setMobileGroupsOpen] = useState({});
  // Мега-меню «Программы» (~960 px) стоит по центру под шапкой, а не под
  // кнопкой: Radix выравнивает от кнопки, поэтому сдвиг считаем при открытии.
  const productsTriggerRef = useRef(null);
  const [megaOffset, setMegaOffset] = useState(0);
  const centerMegaMenu = (open) => {
    if (!open || !productsTriggerRef.current) return;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const width = Math.min(MEGA_MENU_WIDTH, vw - 32);
    setMegaOffset(Math.round((vw - width) / 2 - productsTriggerRef.current.getBoundingClientRect().left));
  };
  // loading — «авторизация ещё не проверена». Пока он поднят, шапка не рисует
  // ни гостя, ни вошедшего: /auth/me отвечает до трёх попыток, и на телефоне
  // это секунды, за которые вошедший человек успевал увидеть гостевое меню.
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  // Пункт ПРО мобильного меню. Один источник на все замки сайта: у человека
  // с правом на 499 это ДВОЙНАЯ ВЫГОДА и оформление за 499, у остальных —
  // прежнее «Открыть ПРО» на витрину тарифов.
  const proCta = useProCta();
  const navigate = useNavigate();
  // На самой главной кнопка «НА ГЛАВНУЮ» ведёт на текущую страницу —
  // занимает место в шапке и ничего не даёт. Прячем её только здесь.
  const isHome = useLocation().pathname === '/';

  // Подпись «НА ГЛАВНУЮ» уступает место имени автора. Полоса шапки не шире
  // 1280 px (max-w-7xl), поэтому от ширины экрана свободное место почти не
  // зависит, и брейкпоинт тут не помогает: меряем. Не хватает места —
  // подпись убираем (остаётся значок), имя «С. Филатова» не сжимается и не
  // режется. Место снова есть — подпись возвращается. Без ResizeObserver
  // (старые браузеры, тесты) — как было.
  const rowRef = useRef(null);
  const leftRef = useRef(null);
  const menuRef = useRef(null);
  const homeLabelRef = useRef(null);
  const homeLabelWidth = useRef(0);
  const [homeLabel, setHomeLabel] = useState(true);
  useLayoutEffect(() => {
    const row = rowRef.current;
    const left = leftRef.current;
    const menu = menuRef.current;
    if (!row || !left || !menu || typeof window === 'undefined' || !window.ResizeObserver) return undefined;
    // Меньше этого зазора между именем и меню — места «нет»: вплотную
    // подпись читается как продолжение имени. Столько же, сколько между
    // пунктами меню (xl:gap-4).
    const MIN_GAP = 16;
    const fit = () => {
      // scrollWidth — ширина содержимого: левая группа может ужаться, а её
      // содержимое (логотип, фото, имя) — нет.
      const free = row.clientWidth - left.scrollWidth - menu.scrollWidth;
      setHomeLabel((shown) => {
        if (shown) {
          if (free >= MIN_GAP) return true;
          const w = homeLabelRef.current ? homeLabelRef.current.offsetWidth : 0;
          homeLabelWidth.current = w + 6; // + gap-1.5 между значком и подписью
          return false;
        }
        return free >= homeLabelWidth.current + MIN_GAP;
      });
    };
    const ro = new window.ResizeObserver(fit);
    [row, left, menu].forEach((el) => ro.observe(el));
    fit();
    return () => ro.disconnect();
  }, [isHome]);
  // У кого ПРО уже есть (в том числе бессрочно) — пункт ПРО в меню лишний.
  const proIsOwned =
    (user?.effective_tier || user?.access_level || 1) >= 3
    || user?.effective_tier_name === 'PRO'
    || !!user?.is_lifetime;

  const toggleMobileMenu = () => {
    setMobileMenuOpen((open) => {
      if (open) setMobileProductsOpen(false);
      return !open;
    });
  };

  // Вход без стены: любой пункт ведёт прямо в программу — гостя тоже
  // (ссылками <Link>, см. меню ниже). Регистрацию и ПРО программа спросит
  // сама, в момент расчёта (AccessGuard → useAccessGate) или на размытых
  // блоках результата.

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
    {/* Сквозная плашка живёт здесь: меню рендерят 17 страниц, значит плашка
        появляется везде, где есть меню, и ровно один раз. */}
    <PromoStickyBar />
    <nav
      className="fixed left-0 right-0 z-50 bg-accent-cta-75 border-b border-sky-200 shadow-sm"
      style={{ top: 'var(--promo-top-h, 0px)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={rowRef} className="flex items-center justify-between h-16">
          {/* Logo */}
          <div ref={leftRef} className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              {/* Логотип берёт название из того же REACT_APP_SITE_NAME, что и
                  <title> (см. src/lib/siteConfig.js). Составное написание
                  сохранено: «АСТРО» + «фил», «ASTRO» + «feel». */}
              {/* Кегль на самых узких экранах на шаг меньше: в полосе рядом
                  теперь стоит кнопка входа, и на 360 px логотип 2xl вместе с
                  ней и гамбургером упирался в край. Кнопку не убираем — она
                  и есть смысл правки. */}
              <span className="font-heading font-extrabold text-xl sm:text-2xl md:text-3xl text-slate-900 tracking-tight">
                {SITE_NAME_PARTS.head}<span className="text-sky-600">{SITE_NAME_PARTS.tail}</span>
              </span>
            </Link>

            {/* Author photo + name — с 1280 px. Clicks the whole block to /about.
                На 1024–1279 блока нет целиком: там полоса не вмещала меню
                вошедшего, и фото сжималось в полоску. */}
            <Link
              to="/about"
              className="hidden xl:flex items-center gap-2 group"
              data-testid="nav-author-block"
            >
              <img width="32" height="32"
                src={AUTHOR_PHOTO}
                alt="Светлана Филатова"
                className="w-8 h-8 shrink-0 rounded-full object-cover border border-sky-200"
              />
              {/* Имя — с 1280 px: на узком десктопе рядом с пунктом «Хорар»
                  шапке не хватало места, и кнопки входа уезжали за край. */}
              <span className="hidden xl:inline shrink-0 whitespace-nowrap font-semibold text-base text-slate-800 group-hover:text-sky-600 transition-colors"
                data-testid="nav-author-name">
                С. Филатова
              </span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div ref={menuRef} className="hidden lg:flex items-center gap-2.5 xl:gap-4">
            {/* Home button */}
            {!isHome && (
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-sky-700 bg-sky-100 hover:bg-sky-200 transition-colors"
                data-testid="nav-home-btn"
                aria-label="На главную"
              >
                <Home className="w-4 h-4" />
                {/* Подпись — с 1280 px и только когда хватает места (см.
                    homeLabel выше); иначе значок, имя автора целое. */}
                <span ref={homeLabelRef} className={homeLabel ? 'hidden xl:inline' : 'hidden'}
                  data-testid="nav-home-label">НА ГЛАВНУЮ</span>
              </Link>
            )}

            {/* Products Dropdown */}
            <DropdownMenu onOpenChange={centerMegaMenu}>
              <DropdownMenuTrigger asChild>
                <Button ref={productsTriggerRef} variant="ghost" className="flex items-center gap-1 text-slate-800 hover:text-sky-600 font-semibold text-base">
                  Программы
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              {/* Мега-меню: четыре колонки по группам, у каждой программы —
                  серая строка «что узнаешь» (menuHint из constants/programs.js). */}
              <DropdownMenuContent
                align="start"
                alignOffset={megaOffset}
                collisionPadding={16}
                className="w-[min(960px,calc(100vw_-_2rem))] max-h-[calc(100vh_-_6rem)] overflow-y-auto p-3"
                data-testid="nav-products-menu"
              >
                <div className="grid grid-cols-4 gap-3">
                  {withOwnerPreview(PROGRAM_GROUPS, user).map((group) => (
                    <div
                      key={group.key}
                      className={`rounded-lg p-1.5 ${(GROUP_STYLE[group.key] || GROUP_STYLE.self).box}`}
                      data-testid={`nav-group-${group.key}`}
                    >
                      <DropdownMenuLabel className="px-2 pt-1 pb-1.5">
                        <GroupTitle group={group} />
                      </DropdownMenuLabel>
                      {/* Пункт — настоящая ссылка <a href> (02.11.2026): адрес
                          видят поисковики, переход работает и без скриптов. */}
                      {group.programs.filter(Boolean).map((product) => (
                        <DropdownMenuItem
                          key={product.key}
                          asChild
                          className="cursor-pointer flex-col items-start gap-0 py-1.5 focus:bg-white/80"
                        >
                          <Link to={product.path} data-testid={`nav-program-${product.key}`}>
                            <span className="font-medium text-slate-800 leading-snug">{product.name}</span>
                            {product.menuHint && (
                              <span className="block w-full truncate text-xs text-slate-500 leading-snug">{product.menuHint}</span>
                            )}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Хорар — отдельным пунктом, не только внутри «Программ»:
                самая частая точка входа и главная посадочная по «хорар
                онлайн». Адрес — только относительный /horar. */}
            <Link
              to="/horar"
              className="text-slate-800 hover:text-sky-600 transition-colors font-semibold text-base"
              data-testid="nav-horar-link"
            >
              Хорар
            </Link>

            {/* Prices */}
            <Link
              to="/pricing"
              className="text-slate-800 hover:text-sky-600 transition-colors font-semibold text-base"
              data-testid="nav-pricing-link"
            >
              Цены
            </Link>

            {/* Consultations */}
            <Link
              to="/consultation"
              className="text-slate-800 hover:text-sky-600 transition-colors font-semibold text-base"
              data-testid="nav-consultation-link"
            >
              Консультации
            </Link>

            {/* Articles */}
            <Link
              to="/stati"
              className="text-slate-800 hover:text-sky-600 transition-colors font-semibold text-base"
              data-testid="nav-articles-link"
            >
              Статьи
            </Link>

            {/* Contacts */}
            <Link to="/contacts" className="text-slate-800 hover:text-sky-600 transition-colors font-semibold text-base">
              Контакты
            </Link>

            {/* Auth Section */}
            {authLoading ? (
              /* Пока не пришёл ответ /auth/me — держим место блока, но не
                 угадываем состояние: ни «Вход/Регистрация», ни имя. */
              <div
                className="h-10 w-40 rounded-lg bg-sky-100/70 animate-pulse"
                aria-hidden="true"
                data-testid="nav-auth-skeleton"
              />
            ) : isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" aria-label={accountLabel(user)}
                    title={accountLabel(user)}>
                    <User className="w-4 h-4" />
                    {/* Подпись — с 1280 px; на 1024–1279 значок и плашка
                        тарифа, иначе меню вошедшего не помещалось в экран. */}
                    <span className="hidden xl:inline max-w-[120px] truncate" data-testid="nav-account-name">{accountLabel(user)}</span>
                    {getStatusBadge(user)}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer">
                    Личный кабинет
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Выйти
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate('/auth/login')}>
                  Вход
                </Button>
                <Button onClick={() => navigate('/auth/register')} className="bg-sky-600 hover:bg-sky-700">
                  Регистрация
                </Button>
              </div>
            )}
          </div>

          {/* Вход на телефоне — В САМОЙ ПОЛОСЕ, рядом с гамбургером.
              Внутри меню он тоже есть, но за два нажатия и за прокрутку:
              человек с телефона, которому надо вернуться в свой кабинет,
              должен видеть вход сразу. Гостю — «Войти», вошедшему —
              «Кабинет» на тот же адрес, что пункт «Личный кабинет» в меню.
              Пока /auth/me не ответил, держим место заглушкой того же
              размера: иначе шапка прыгает и вошедший на секунду видит
              «Войти». Тот же приём, что у блока входа на ПК выше. */}
          <div className="lg:hidden flex items-center gap-1">
            {authLoading ? (
              <div
                className="h-9 w-24 rounded-lg bg-sky-100/70 animate-pulse"
                aria-hidden="true"
                data-testid="nav-mobile-auth-skeleton"
              />
            ) : isAuthenticated ? (
              <Link to="/account" data-testid="nav-mobile-account-btn">
                <Button size="sm" variant="outline" className="h-9 gap-1.5 bg-white">
                  <User className="w-4 h-4" />
                  Кабинет
                </Button>
              </Link>
            ) : (
              <Link to="/auth/login" data-testid="nav-mobile-login-btn">
                <Button size="sm" className="h-9 gap-1.5 bg-sky-600 hover:bg-sky-700">
                  <LogIn className="w-4 h-4" />
                  Войти
                </Button>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              className="p-2 rounded-lg hover:bg-slate-100"
              onClick={toggleMobileMenu}
              aria-label={mobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
              data-testid="nav-mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Счётчик подарочных дней — ПОД строкой меню, во всю ширину. Высота
          полосы входит в --promo-bar-h, поэтому страницы не подлезают под
          неё; меню само сдвигается только на плашку над ним (--promo-top-h). */}
      <ProDaysStrip />

      {/* Mobile Menu — тот же состав, что и в верхнем меню на десктопе:
          вход в кабинет, На главную, Программы, Цены, Консультации, Статьи,
          Контакты. «На главную» не показывается на самой главной. */}
      {mobileMenuOpen && (
        /* Меню — потомок <nav position:fixed>, поэтому со страницей оно не
           едет: всё, что не поместилось в экран, за нижним краем недостижимо
           никаким жестом (палец прокручивает документ, а меню к документу не
           привязано). На 360x640 нижняя ЛАЙТ- и вся ПРО-группа уходили за
           край на 328px, на 360x560 — на 408px. Поэтому меню ограничено
           видимой высотой (экран минус промо-плашка минус 64px шапки) и
           прокручивается само.
           dvh под @supports — чтобы на айфоне считалась реально видимая
           область, а не экран со спрятанной адресной строкой; где dvh нет,
           остаётся vh, и хуже нынешнего не становится.
           overscroll-contain не даёт прокрутке меню перетекать на страницу,
           когда список домотан до конца. Полноценной блокировки фона в
           проекте нет — это отдельная правка. */
        <div className="lg:hidden bg-white border-t border-slate-100 py-4 px-4 max-h-[calc(100vh_-_var(--promo-bar-h,0px)_-_4rem)] supports-[height:100dvh]:max-h-[calc(100dvh_-_var(--promo-bar-h,0px)_-_4rem)] overflow-y-auto overscroll-contain">
          {/* Вход в кабинет — ПЕРВЫМ пунктом. На экране 390 px всё, что стоит
              под списком программ, оказывается ниже сгиба: человек открывал
              гамбургер, видел калькуляторы и решал, что входа нет. */}
          <div className="pb-4 mb-4 border-b border-slate-100" data-testid="mobile-auth-block">
            {authLoading ? (
              /* Авторизация ещё проверяется — не рисуем ни гостя, ни вошедшего.
                 Место занято, чтобы меню не прыгало, когда придёт ответ. */
              <div
                className="h-11 w-full rounded-lg bg-slate-100 animate-pulse"
                aria-hidden="true"
                data-testid="mobile-auth-skeleton"
              />
            ) : isAuthenticated ? (
              <>
                <Button
                  className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-base font-semibold"
                  onClick={() => { navigate('/account'); setMobileMenuOpen(false); }}
                  data-testid="mobile-nav-account-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Личный кабинет
                </Button>
                <div className="flex items-center justify-between gap-2 mt-3">
                  {user?.name?.trim() && (
                    <span className="text-sm text-slate-700 truncate max-w-[180px]" data-testid="mobile-nav-account-name">
                      {user.name.trim()}
                    </span>
                  )}
                  <span className="ml-auto">{getStatusBadge(user)}</span>
                </div>
                <Button
                  variant="ghost"
                  className="w-full mt-1 text-red-600"
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  data-testid="mobile-nav-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Выйти
                </Button>
              </>
            ) : (
              <>
                <Button
                  className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-base font-semibold"
                  onClick={() => { navigate('/auth/login'); setMobileMenuOpen(false); }}
                  data-testid="mobile-nav-login-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Войти
                </Button>
                <Button
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => { navigate('/auth/register'); setMobileMenuOpen(false); }}
                  data-testid="mobile-nav-register-btn"
                >
                  Регистрация
                </Button>
              </>
            )}
          </div>

          {!isHome && (
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-sm font-semibold text-sky-700 bg-sky-100"
              data-testid="mobile-nav-home-btn"
            >
              <Home className="w-4 h-4" />
              НА ГЛАВНУЮ
            </Link>
          )}

          {/* Программы — раскрывающийся пункт. Внутри тот же список программ,
              что и в выпадающем меню на десктопе, теми же адресами. */}
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setMobileProductsOpen((open) => !open)}
              aria-expanded={mobileProductsOpen}
              className="w-full flex items-center justify-between px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg font-semibold"
              data-testid="mobile-nav-products-toggle"
            >
              <span>Программы</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileProductsOpen && (
              <div className="mt-1 pl-2" data-testid="mobile-nav-products-list">
                {withOwnerPreview(PROGRAM_GROUPS, user).map((group) => {
                  const groupOpen = !!mobileGroupsOpen[group.key];
                  return (
                    <div
                      key={group.key}
                      className={`mt-2 first:mt-0 rounded-lg ${(GROUP_STYLE[group.key] || GROUP_STYLE.self).box}`}
                      data-testid={`mobile-nav-group-${group.key}`}
                    >
                      <button
                        type="button"
                        onClick={() => setMobileGroupsOpen((o) => ({ ...o, [group.key]: !o[group.key] }))}
                        aria-expanded={groupOpen}
                        className="w-full flex items-center justify-between px-3 py-2.5"
                        data-testid={`mobile-nav-acc-${group.key}`}
                      >
                        <GroupTitle group={group} />
                        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${groupOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {groupOpen && (
                        <div className="px-1 pb-1.5">
                          {group.programs.filter(Boolean).map((product) => (
                            <Link
                              key={product.key}
                              to={product.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className="w-full flex flex-col items-start px-3 py-1.5 text-left hover:bg-white/80 rounded-md"
                              data-testid={`mobile-nav-program-${product.key}`}
                            >
                              <span className="text-slate-800 leading-snug">{product.name}</span>
                              {product.menuHint && (
                                <span className="block w-full truncate text-xs text-slate-500 leading-snug">{product.menuHint}</span>
                              )}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                ))}
              </div>
            )}
          </div>

          {/* Остальные разделы верхнего меню */}
          <div className="space-y-1">
            {/* Вход в ПРО. ПРО и бессрочному его не показываем — продавать
                нечего; пока право на акцию неизвестно, пункта тоже нет,
                иначе цена мигнёт с 999 на 499 на глазах у человека. */}
            {proCta.ready && !proIsOwned && (
              <Link
                to={proCta.path}
                onClick={() => {
                  if (proCta.isDoubleBenefit) {
                    import('@/lib/analytics')
                      .then(({ trackEvent }) => trackEvent('double_benefit_click', { place: 'mobile_menu' }))
                      .catch(() => {});
                  }
                  setMobileMenuOpen(false);
                }}
                className={`block px-3 py-2 rounded-lg font-semibold ${
                  proCta.isOffer
                    ? 'text-accent-pro-700 bg-accent-pro-50 hover:bg-accent-pro-100'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
                data-testid="mobile-nav-pro-link"
              >
                {proCta.label}
              </Link>
            )}
            <Link
              to="/horar"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg font-semibold"
              data-testid="mobile-nav-horar-link"
            >
              Хорар
            </Link>
            <Link
              to="/pricing"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg font-semibold"
              data-testid="mobile-nav-pricing-link"
            >
              Цены
            </Link>
            <Link
              to="/consultation"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg font-semibold"
              data-testid="mobile-nav-consultation-link"
            >
              Консультации
            </Link>
            <Link
              to="/stati"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg font-semibold"
              data-testid="mobile-nav-articles-link"
            >
              Статьи
            </Link>
            <Link
              to="/contacts"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg font-semibold"
              data-testid="mobile-nav-contacts-link"
            >
              Контакты
            </Link>
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg"
              data-testid="mobile-nav-about-link"
            >
              <img width="24" height="24" src={AUTHOR_PHOTO} alt="Светлана Филатова" className="w-6 h-6 rounded-full object-cover" />
              <span className="font-semibold">С. Филатова</span>
            </Link>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default MainNavbar;
